/**
 * CR481: CardSnippetBlock — Configurable card snippet with eligibility gating,
 * fallback rendering, and admin-only warnings.
 */

import React from "react";
import { FileText, AlertTriangle, ImageIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import type { DealCardBlockProps } from "@/lib/deal-cards/specV2/types";
import type { Deal } from "@/lib/types/deals";
import { FlyerPreview } from "@/components/deals/FlyerPreview";
import { Badge } from "@/components/ui/badge";
import {
  DEFAULT_CARD_SNIPPET_CONFIG,
  evaluateSnippetEligibility,
  extractSnippetMeta,
  getCornerRadiusClass,
  getBorderClass,
  getAspectClass,
  type CardSnippetConfig,
} from "@/lib/deal-cards/cardSnippetConfig";

export const CardSnippetBlock: React.FC<DealCardBlockProps<Deal>> = ({ deal, block, context }) => {
  if (!block?.enabled) return null;

  const isAdmin = context?.isAdmin ?? false;
  const config: CardSnippetConfig = {
    ...DEFAULT_CARD_SNIPPET_CONFIG,
    ...(block.config as Partial<CardSnippetConfig> | undefined),
  };

  const meta = extractSnippetMeta(deal);
  const { eligible, reason } = evaluateSnippetEligibility(meta, config, isAdmin);

  // Presentation classes
  const radiusClass = getCornerRadiusClass(config.corner_radius);
  const borderClass = getBorderClass(config.show_border, config.border_style);
  const aspectClass = getAspectClass(config.aspect_ratio);

  // ---- ELIGIBLE: show snippet ----
  if (eligible) {
    return (
      <div
        data-block="card_snippet"
        data-deal-id={(deal as any)?.id ?? ""}
        className={cn("relative w-full my-1 overflow-hidden", radiusClass, borderClass)}
      >
        <FlyerPreview deal={deal as any} aspect={aspectClass} />
        {config.show_snippet_badge && config.snippet_badge_text && (
          <Badge
            variant="secondary"
            className="absolute top-2 left-2 text-[10px] bg-background/80 backdrop-blur-sm"
          >
            <FileText className="h-3 w-3 mr-1" />
            {config.snippet_badge_text}
          </Badge>
        )}
      </div>
    );
  }

  // ---- NOT ELIGIBLE: admin warning if snippet exists but rejected ----
  const hasSnippetButRejected = meta?.snippet_asset_path && !eligible;

  if (isAdmin && hasSnippetButRejected && config.show_warning_bad_bbox) {
    return (
      <div
        data-block="card_snippet"
        data-deal-id={(deal as any)?.id ?? ""}
        className={cn("w-full my-1 p-3 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800", radiusClass)}
      >
        <div className="flex items-start gap-2">
          <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
          <div className="text-xs space-y-0.5">
            <p className="font-medium text-amber-800 dark:text-amber-300">
              Snippet available but not eligible
            </p>
            <p className="text-amber-600 dark:text-amber-400">
              {reason === "no_asset_path" ? "No snippet asset path" :
               reason.startsWith("rejected_") ? `BBox rejected: ${meta?.bbox_reject_reason}` :
               reason.startsWith("quality_") ? `Quality: ${meta?.bbox_quality_hint} (min: ${config.min_bbox_quality})` :
               reason.startsWith("status_") ? `Status: ${meta?.snippet_status}` :
               reason}
            </p>
            {meta?.bbox_area_ratio != null && (
              <p className="text-amber-500 dark:text-amber-500 font-mono">
                area_ratio: {meta.bbox_area_ratio.toFixed(3)}
              </p>
            )}
            {/* CR486: Scoring details in admin */}
            {meta?.bbox_score != null && (
              <p className="text-amber-500 dark:text-amber-500 font-mono">
                score: {meta.bbox_score.toFixed(2)}
                {meta.bbox_score_components && (
                  <span className="ml-1 text-amber-400">
                    (hits: {meta.bbox_score_components.key_phrase_hits}
                    {" · "}area_pen: {meta.bbox_score_components.area_penalty.toFixed(2)}
                    {" · "}overlap_pen: {meta.bbox_score_components.overlap_penalty.toFixed(2)})
                  </span>
                )}
              </p>
            )}
            {meta?.bbox_locator_pass && (
              <p className="text-amber-500 dark:text-amber-500 font-mono text-[10px]">
                pass: {meta.bbox_locator_pass}
                {meta.locator_reason && ` · ${meta.locator_reason}`}
              </p>
            )}
          </div>
        </div>
        {/* Still render fallback below warning */}
        <FallbackContent config={config} deal={deal} aspectClass={aspectClass} radiusClass={radiusClass} borderClass={borderClass} />
      </div>
    );
  }

  // ---- FALLBACK ----
  return (
    <FallbackContent
      config={config}
      deal={deal}
      aspectClass={aspectClass}
      radiusClass={radiusClass}
      borderClass={borderClass}
    />
  );
};

// ================================================================
// FALLBACK COMPONENT
// ================================================================

function FallbackContent({
  config,
  deal,
  aspectClass,
  radiusClass,
  borderClass,
}: {
  config: CardSnippetConfig;
  deal: Deal;
  aspectClass: string;
  radiusClass: string;
  borderClass: string;
}) {
  if (config.fallback_mode === "none") return null;

  if (config.fallback_mode === "generic_placeholder") {
    return (
      <div
        data-block="card_snippet"
        data-fallback="generic_placeholder"
        className={cn(
          "w-full my-1 flex flex-col items-center justify-center bg-muted/30 border-2 border-dashed border-muted-foreground/30",
          aspectClass,
          radiusClass,
        )}
      >
        <ImageIcon className="h-8 w-8 text-muted-foreground/50 mb-1" />
        <span className="text-xs text-muted-foreground">No flyer snippet</span>
      </div>
    );
  }

  // brand_artwork / issuer_artwork: attempt to find artwork URL
  const dealAny = deal as any;
  let artworkUrl: string | null = null;

  if (config.fallback_mode === "brand_artwork") {
    artworkUrl =
      dealAny?.reward_brand_artwork_url ??
      dealAny?.purchase_brand_artwork_url ??
      dealAny?.brand_artwork_url ??
      dealAny?.canonical_deal_json?.reward_brand_artwork_url ??
      null;
  } else if (config.fallback_mode === "issuer_artwork") {
    artworkUrl =
      dealAny?.issuer_artwork_url ??
      dealAny?.canonical_deal_json?.issuer_artwork_url ??
      null;
  }

  if (!artworkUrl) {
    // No artwork found → show generic placeholder instead of nothing
    return (
      <div
        data-block="card_snippet"
        data-fallback={config.fallback_mode}
        className={cn(
          "w-full my-1 flex flex-col items-center justify-center bg-muted/30 border-2 border-dashed border-muted-foreground/30",
          aspectClass,
          radiusClass,
        )}
      >
        <ImageIcon className="h-8 w-8 text-muted-foreground/50 mb-1" />
        <span className="text-xs text-muted-foreground">Deal preview</span>
      </div>
    );
  }

  return (
    <div
      data-block="card_snippet"
      data-fallback={config.fallback_mode}
      className={cn("w-full my-1 overflow-hidden", radiusClass, borderClass)}
    >
      <img
        src={artworkUrl}
        alt="Deal artwork"
        className={cn("w-full h-auto object-contain", aspectClass)}
        loading="lazy"
        draggable={false}
      />
    </div>
  );
}

export default CardSnippetBlock;
